
import { Inject, Injectable, NgZone, Optional, DOCUMENT } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CONSTANTS, Constants } from 'lib-trend-core';
import { ChatSwitcherComponent } from './chat-switcher.component';
import { ChatDialogService } from '../services/chat-dialog.service';

/**
 * Quick-switcher activation (SPEC-2026-07-internal-chat, T-22 / RF-21 / AC-17).
 *
 * Registers ONE document-level `Ctrl/Cmd+K` listener for the whole
 * authenticated shell, so the switcher opens from the `/chat` full view, from
 * the bubble popup and from any other route — without every surface wiring its
 * own HostListener. `init()` is called by the eager ChatStateModule (the same
 * always-mounted seam that registers the chat store).
 *
 * Self-gated exactly like the other chat shell surfaces: no session or
 * `moduleChat` off ⇒ the shortcut is ignored and the browser keeps its default
 * behavior.
 */
@Injectable({ providedIn: 'root' })
export class ChatSwitcherService {

  private registered = false;
  private dialogRef: MatDialogRef<ChatSwitcherComponent> | null = null;

  constructor(
    private readonly dialog: ChatDialogService,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document,
    @Optional() @Inject(CONSTANTS) private readonly constants: Constants | null,
  ) { }

  /** Idempotent — safe to call from every mounting surface. */
  init(): void {
    if (this.registered) return;
    this.registered = true;
    this.document.addEventListener('keydown', this.onKeydown);
  }

  /** Opens the overlay (no-op when it is already open). */
  open(): void {
    if (this.dialogRef) return;

    this.dialogRef = this.dialog.open(ChatSwitcherComponent, {
      width: '560px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      panelClass: 'chat-switcher-dialog',
    });

    this.dialogRef.afterClosed().subscribe(() => this.dialogRef = null);
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || event.altKey || event.shiftKey) return;
    if (!(event.metaKey || event.ctrlKey)) return;
    if ((event.key ?? '').toLowerCase() !== 'k') return;
    if (!this.isAuthenticated() || !this.hasModuleChat()) return;

    event.preventDefault();
    this.zone.run(() => this.open());
  };

  /** Same storage AbstractComponent.getCurrentUserUser() reads. */
  private readStoredSession(): any {
    try {
      const storageKey = this.constants?.['currentUser'];
      if (!storageKey) return null;
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private isAuthenticated(): boolean {
    return !!this.readStoredSession()?.token;
  }

  private hasModuleChat(): boolean {
    return this.readStoredSession()?.user?.company?.modules?.moduleChat ?? false;
  }
}
